import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { Translation } from "vanilla-cookieconsent";

import { ConsentManagerConfigSchema } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { ConsentManagementConfigVersion } from "@/api";
import { useDeepMemo } from "@/hooks/useDeepMemo";
import { sanitizeConsentCustomCss } from "@/consent-management/utils/sanitizeConsentCustomCss";
import { resolveConsentModalDismissButtonColor } from "@/consent-management/utils/freshpaint";

interface FreshpaintConsentManagerPreviewProps {
  consentManagementConfig: ConsentManagementConfigVersion;
  gpcDetected?: boolean;
}

const PREVIEW_COOKIE_NAME = "preview-consent-cookie";

/**
 * Shared by the iframe, its scroll container, and the dialog's suppression
 * notice so switching preview scenarios never changes the pane's height.
 */
export const PREVIEW_PANE_HEIGHT_CLASS = "h-112";

/**
 * Every config change reloads the preview iframe from a fresh blob URL, so
 * previews fed by live form state debounce their input to avoid tearing the
 * document down on each keystroke. Previews of a fixed config skip this.
 */
export const PREVIEW_DEBOUNCE_MS = 300;
const GPC_PREFERENCES_SECTION = {
  title: "Opt-Out Signal Detected",
  description:
    "Your browser is sending a Global Privacy Control signal. Your opt-out preference is being honored.",
};
const GPC_CONSENT_MODAL_FOOTER =
  "<strong>Opt-Out Signal Detected:</strong> Your opt-out preference is being honored.";

export function FreshpaintConsentManagerPreview({
  consentManagementConfig,
  gpcDetected = false,
}: FreshpaintConsentManagerPreviewProps) {
  const config = useDeepMemo(consentManagementConfig);

  return (
    <div className="space-y-3">
      <div
        className={clsx(
          "relative overflow-auto rounded-xl",
          PREVIEW_PANE_HEIGHT_CLASS,
        )}
      >
        <ConsentManagerPreviewIframe
          config={config}
          gpcDetected={gpcDetected}
        />
      </div>
    </div>
  );
}

interface ConsentManagerPreviewIframeProps {
  config: ConsentManagementConfigVersion;
  gpcDetected?: boolean;
}

export function ConsentManagerPreviewIframe({
  config,
  gpcDetected = false,
}: ConsentManagerPreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const iframeSrc = useMemo(() => {
    const c = {
      ...config.freshpaintConfig,
      // The preview always shows the modal, even when the customer has turned
      // off auto-show for real visitors — otherwise the pane renders empty.
      autoShow: true,
      language: getDynamicCopy(config.freshpaintConfig, gpcDetected),
      cookie: {
        name: PREVIEW_COOKIE_NAME,
      },
    };

    const htmlContent = generatePreviewHTML(c);
    const blob = new Blob([htmlContent], { type: "text/html" });
    return URL.createObjectURL(blob);
  }, [config, gpcDetected]);

  // Cleanup blob URL on unmount or when src changes
  useEffect(() => {
    return () => {
      if (iframeSrc.startsWith("blob:")) {
        URL.revokeObjectURL(iframeSrc);
      }
    };
  }, [iframeSrc]);

  return (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      className={clsx("w-full border-0", PREVIEW_PANE_HEIGHT_CLASS)}
      title="Consent manager preview"
    />
  );
}

function generatePreviewHTML(config: any): string {
  // Get the CDN URLs for vanilla-cookieconsent
  // These should match what customers would use in production
  const cookieConsentCSS =
    "https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@v3.1.0/dist/cookieconsent.css";
  const cookieConsentJS =
    "https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@v3.1.0/dist/cookieconsent.umd.js";

  // Serialize config, handling functions and circular references
  const serializedConfig = JSON.stringify(config, (key, value) => {
    // Skip functions as they can't be serialized
    if (typeof value === "function") {
      return undefined;
    }
    return value;
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Consent Manager Preview</title>
  <link rel="stylesheet" href="${cookieConsentCSS}">
  <style>
    html, body {
      margin: 0;
      padding: 0;
      background: transparent;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      height: 100%;
      overflow: auto !important;
    }
    .cm-wrapper:before {
      display: none !important;
    }
    #cc-main {
      position: static !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      min-height: 100% !important;
      padding: 1.5rem 1rem !important;
    }
    .cm-wrapper {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
    }
    .cm {
      position: static !important;
      transform: none !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
    ${generateColorStyles(config.colorStyles || {})}
    ${generateDisplayStyles(config)}
    ${generateCustomCssStyles(config)}
  </style>
</head>
<body>
  <script src="${cookieConsentJS}"></script>
  <script>
    (function() {
      // This document reloads on every keystroke in the config form, and
      // cookieconsent focuses the modal whenever it opens. Left alone that
      // pulls the caret out of the field being edited, so swallow programmatic
      // focus until the reader interacts with the preview itself. Native focus
      // movement (clicking, tabbing) is untouched, and each reload starts over.
      var nativeFocus = HTMLElement.prototype.focus;
      HTMLElement.prototype.focus = function() {};
      ['pointerdown', 'keydown'].forEach(function(eventName) {
        document.addEventListener(eventName, function() {
          HTMLElement.prototype.focus = nativeFocus;
        }, { capture: true, once: true });
      });

      const config = ${serializedConfig};
      var previewDismissClicked = false;

      config.onModalReady = function(_ref) {
        var modalName = _ref.modalName;
        var modal = _ref.modal;
        if (modalName !== "consentModal" || !config.enableConsentModalDismiss) {
          return;
        }
        var cmBody = modal.querySelector(".cm__body");
        if (!cmBody || modal.querySelector(".fp-dismiss-btn")) {
          return;
        }
        var dismissBtn = document.createElement("button");
        dismissBtn.type = "button";
        dismissBtn.className = "fp-dismiss-btn";
        dismissBtn.setAttribute("aria-label", "Close");
        dismissBtn.textContent = "\\u00D7";
        dismissBtn.addEventListener("click", function() {
          previewDismissClicked = true;
          if (window.CookieConsent) {
            CookieConsent.hide();
          }
        });
        cmBody.insertBefore(dismissBtn, cmBody.firstChild);
      };

      // Re-show after hide for preview, except when the user dismissed the consent modal.
      config.onModalHide = function() {
        if (previewDismissClicked) {
          previewDismissClicked = false;
          return;
        }
        if (window.CookieConsent) {
          CookieConsent.eraseCookies('${PREVIEW_COOKIE_NAME}');
          setTimeout(function() {
            CookieConsent.show();
          }, 100);
        }
      };

      // Initialize cookie consent when script loads
      function initCookieConsent() {
        if (window.CookieConsent) {
          CookieConsent.run(config);
        }
      }

      // Try to initialize immediately if script is already loaded
      if (window.CookieConsent) {
        initCookieConsent();
      } else {
        // Wait for script to load
        window.addEventListener('load', initCookieConsent);
      }
    })();
  </script>
</body>
</html>`;
}

function generateColorStyles(colorStyles: Record<string, string>): string {
  if (!colorStyles || Object.keys(colorStyles).length === 0) {
    return "";
  }

  const variableRules = Object.entries(colorStyles)
    .map(([key, value]) => `  ${key}: ${String(value)};`)
    .join("\n");

  return `:root {\n${variableRules}\n}\n`;
}

/**
 * Escape HTML-special characters so admin-provided copy is rendered as literal
 * text inside the preview iframe rather than being parsed as markup.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function getDynamicCopy(
  fpConsentManagerConfig: ConsentManagerConfigSchema["freshpaintConfig"],
  gpcDetected: boolean,
):
  | NonNullable<ConsentManagerConfigSchema["freshpaintConfig"]>["language"]
  | undefined {
  if (!fpConsentManagerConfig || !fpConsentManagerConfig?.language) {
    return undefined;
  }
  const privacyPolicyUrl =
    fpConsentManagerConfig.privacyPolicyUrl?.trim() ||
    fpConsentManagerConfig.language.privacyPolicyUrl?.trim();
  const clonedTranslations = JSON.parse(
    JSON.stringify(fpConsentManagerConfig.language.translations),
  );
  const translations = {
    ...clonedTranslations,
  };
  const privacyPolicyLinkText = escapeHtml(
    fpConsentManagerConfig.privacyPolicyLinkText?.trim() || "Privacy policy",
  );

  for (const translationKey in translations) {
    const translation = translations[translationKey] as Translation;
    if (gpcDetected && translation.preferencesModal?.sections) {
      translation.preferencesModal.sections = [
        { ...GPC_PREFERENCES_SECTION },
        ...translation.preferencesModal.sections,
      ];
    }

    const footerParts: string[] = [];
    if (gpcDetected) {
      footerParts.push(GPC_CONSENT_MODAL_FOOTER);
    }
    if (privacyPolicyUrl) {
      footerParts.push(
        `<a href="${encodeURI(privacyPolicyUrl)}" target="_blank" rel="noopener noreferrer">${privacyPolicyLinkText}</a>`,
      );
    }
    if (footerParts.length > 0 && translation.consentModal) {
      translation.consentModal.footer = footerParts.join(" ");
    }
  }

  return {
    ...fpConsentManagerConfig.language,
    translations,
  };
}

function generateDisplayStyles(
  freshpaintConfig: ConsentManagerConfigSchema["freshpaintConfig"],
) {
  let markup = ``;
  if (freshpaintConfig?.hideConsentModalRejectAll) {
    markup += `
      #cc-main .cm [data-role="necessary"] {
        display: none;
      }
    `;
  }
  if (freshpaintConfig?.enableConsentModalDismiss) {
    const dismissColor =
      resolveConsentModalDismissButtonColor(freshpaintConfig);
    markup += `
      #cc-main .fp-dismiss-btn{background:var(--cc-bg);border:none;border-radius:0;border-bottom-left-radius:var(--cc-btn-border-radius);color:${dismissColor};cursor:pointer;font-size:1.4em;height:42px;line-height:42px;overflow:hidden;padding:0;position:absolute;right:0;text-align:center;top:0;transition:color .15s ease;width:42px;z-index:1}
      #cc-main .fp-dismiss-btn:hover{color:var(--cc-secondary-color)}
      #cc-main .fp-dismiss-btn~.cm__texts .cm__title{padding-right:3rem}
      #cc-main.cc--rtl .fp-dismiss-btn{border-bottom-left-radius:0;border-bottom-right-radius:var(--cc-btn-border-radius);left:0;right:unset}
      #cc-main.cc--rtl .fp-dismiss-btn~.cm__texts .cm__title{padding-left:3rem;padding-right:1.3rem}
    `;
  }
  return markup;
}

function generateCustomCssStyles(
  freshpaintConfig: ConsentManagerConfigSchema["freshpaintConfig"],
) {
  const raw = freshpaintConfig?.customCSS?.trim() ?? "";
  if (!raw) return "";
  // Defense in depth when preview HTML embeds CSS inside <style>
  return sanitizeConsentCustomCss(raw);
}
